import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createPost, fetchMyPosts, fetchPostTags, fetchPosts, getPostErrorMessage } from '@/apis/posts';

const listOptions = options => typeof options === 'object' && options !== null ? { page: options.page || 1, filters: options.filters || {} } : { page: options || 1, filters: {} };

export const loadPosts = createAsyncThunk(
  'posts/loadList',
  async (options = 1, { rejectWithValue }) => {
    try {
      const { page, filters } = listOptions(options);
      const result = await fetchPosts({ page, pageSize: 10, ordering: '-created_at', ...filters });
      if (!Array.isArray(result?.data) || !result?.pagination) {
        throw new Error('帖子列表格式不正确');
      }
      return result;
    } catch (error) {
      return rejectWithValue(getPostErrorMessage(error, '帖子加载失败，请稍后重试'), { status: error.response?.status });
    }
  },
  {
    condition: (options, { getState }) => getState().posts.listStatus !== 'loading' || JSON.stringify(getState().posts.listFilters) !== JSON.stringify(listOptions(options).filters),
  }
);

export const loadMyPosts = createAsyncThunk(
  'posts/loadMyList',
  async (options = 1, { rejectWithValue }) => {
    try {
      const { page, filters } = listOptions(options);
      const result = await fetchMyPosts({ page, pageSize: 10, ...filters });
      if (!Array.isArray(result?.data) || !result?.pagination) {
        throw new Error('我的帖子列表格式不正确');
      }
      return result;
    } catch (error) {
      return rejectWithValue(getPostErrorMessage(error, '我的帖子加载失败，请稍后重试'));
    }
  },
  {
    condition: (options, { getState }) => getState().posts.myStatus !== 'loading' || JSON.stringify(getState().posts.myFilters) !== JSON.stringify(listOptions(options).filters),
  }
);

export const loadPostTags = createAsyncThunk(
  'posts/loadTags',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchPostTags();
    } catch (error) {
      return rejectWithValue(getPostErrorMessage(error, '标签加载失败'));
    }
  },
  {
    condition: (_, { getState }) => {
      const status = getState().posts.tagsStatus;
      return status !== 'loading' && status !== 'succeeded';
    },
  }
);

export const publishPost = createAsyncThunk(
  'posts/publish',
  async (postData, { rejectWithValue }) => {
    try {
      return await createPost(postData);
    } catch (error) {
      return rejectWithValue(getPostErrorMessage(error, '发布失败，请稍后重试'));
    }
  }
);

const postsSlice = createSlice({
  name: 'posts',
  initialState: {
    list: [],
    listPagination: null,
    listStatus: 'idle',
    listError: null,
    listErrorStatus: null,
    listRequestedPage: 1,
    listFilters: {},
    listActiveRequestId: null,
    myList: [],
    myPagination: null,
    myStatus: 'idle',
    myError: null,
    myRequestedPage: 1,
    myActiveRequestId: null,
    myFilters: {},
    tags: [],
    tagsStatus: 'idle',
    tagsError: null,
    publishStatus: 'idle',
  },
  reducers: {
    invalidatePostFeed(state) {
      state.listStatus = 'idle';
      state.listActiveRequestId = null;
    },
    syncPostMetrics(state, action) {
      const { postId, post: updatedPost, removed, like_count, comment_count, is_liked, is_favorited } = action.payload;
      if (removed) {
        state.list = state.list.filter(post => String(post.id) !== String(postId));
        state.myList = state.myList.filter(post => String(post.id) !== String(postId));
        return;
      }
      [state.list, state.myList].forEach(posts => {
        const post = posts.find(item => String(item.id) === String(postId));
        if (!post) return;
        if (updatedPost) Object.assign(post, updatedPost);
        if (typeof like_count === 'number') post.like_count = like_count;
        if (typeof comment_count === 'number') post.comment_count = comment_count;
        if (typeof is_liked === 'boolean') post.is_liked = is_liked;
        if (typeof is_favorited === 'boolean') post.is_favorited = is_favorited;
      });
    },
  },
  extraReducers: builder => {
    builder
      .addCase('user/clearUserState', state => {
        state.list = []; state.listPagination = null; state.listStatus = 'idle'; state.listActiveRequestId = null; state.listFilters = {};
        state.myList = [];
        state.myPagination = null;
        state.myStatus = 'idle';
        state.myError = null;
        state.myRequestedPage = 1;
        state.myActiveRequestId = null;
        state.myFilters = {};
      })
      .addCase('user/setAuthenticated', state => {
        state.list = []; state.listPagination = null; state.listStatus = 'idle'; state.listActiveRequestId = null; state.listFilters = {};
        state.myList = []; state.myPagination = null; state.myStatus = 'idle'; state.myActiveRequestId = null;
      })
      .addCase(loadPosts.pending, (state, action) => {
        const { page, filters } = listOptions(action.meta.arg);
        if (JSON.stringify(state.listFilters) !== JSON.stringify(filters)) { state.list = []; state.listPagination = null; }
        state.listStatus = 'loading';
        state.listError = null;
        state.listErrorStatus = null;
        state.listRequestedPage = page;
        state.listFilters = filters;
        state.listActiveRequestId = action.meta.requestId;
      })
      .addCase(loadPosts.fulfilled, (state, action) => {
        if (state.listActiveRequestId !== action.meta.requestId) return;
        state.listActiveRequestId = null;
        state.listStatus = 'succeeded';
        state.listPagination = action.payload.pagination;
        if (action.payload.pagination.page === 1) {
          state.list = action.payload.data;
        } else {
          const existingIds = new Set(state.list.map(post => post.id));
          state.list.push(...action.payload.data.filter(post => !existingIds.has(post.id)));
        }
      })
      .addCase(loadPosts.rejected, (state, action) => {
        if (action.meta.condition || state.listActiveRequestId !== action.meta.requestId) return;
        state.listActiveRequestId = null;
        state.listStatus = 'failed';
        state.listErrorStatus = action.meta.status || null;
        if ([401, 403, 409].includes(action.meta.status)) { state.list = []; state.listPagination = null; }
        state.listError = action.payload || '帖子加载失败，请稍后重试';
      })
      .addCase(loadMyPosts.pending, (state, action) => {
        const { page, filters } = listOptions(action.meta.arg);
        state.myStatus = 'loading';
        state.myError = null;
        state.myRequestedPage = page;
        state.myFilters = filters;
        state.myActiveRequestId = action.meta.requestId;
        if (state.myRequestedPage === 1) {
          state.myList = [];
          state.myPagination = null;
        }
      })
      .addCase(loadMyPosts.fulfilled, (state, action) => {
        if (state.myActiveRequestId !== action.meta.requestId) return;
        state.myActiveRequestId = null;
        state.myStatus = 'succeeded';
        state.myPagination = action.payload.pagination;
        if (action.payload.pagination.page === 1) {
          state.myList = action.payload.data;
        } else {
          const existingIds = new Set(state.myList.map(post => post.id));
          state.myList.push(...action.payload.data.filter(post => !existingIds.has(post.id)));
        }
      })
      .addCase(loadMyPosts.rejected, (state, action) => {
        if (action.meta.condition || state.myActiveRequestId !== action.meta.requestId) return;
        state.myActiveRequestId = null;
        state.myStatus = 'failed';
        state.myError = action.payload || '我的帖子加载失败，请稍后重试';
      })
      .addCase(loadPostTags.pending, state => {
        state.tagsStatus = 'loading';
        state.tagsError = null;
      })
      .addCase(loadPostTags.fulfilled, (state, action) => {
        state.tagsStatus = 'succeeded';
        state.tags = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(loadPostTags.rejected, (state, action) => {
        if (action.meta.condition) return;
        state.tagsStatus = 'failed';
        state.tagsError = action.payload || '标签加载失败';
      })
      .addCase(publishPost.pending, state => {
        state.publishStatus = 'loading';
      })
      .addCase(publishPost.fulfilled, state => {
        state.publishStatus = 'succeeded';
      })
      .addCase(publishPost.rejected, state => {
        state.publishStatus = 'failed';
      });
  },
});

export const { syncPostMetrics, invalidatePostFeed } = postsSlice.actions;
export default postsSlice.reducer;
